/** Admin — Active Suppliers: manual creation, services price list, contracts. */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Store, X, Mail, Power, Trash2, Loader2, ExternalLink, FileText, Tag, Plus, Check,
} from "lucide-react";
import api from "@/lib/api";

const FIELD = "w-full h-10 px-3 bg-[#050505] border border-white/10 rounded-lg text-sm text-white placeholder:text-zinc-600 focus:border-[#C5A059] outline-none";
export const CATEGORY_LABEL = {
  boats: "Boats", quads_scooters: "Quads & Scooters", rent_a_car: "Rent-a-Car",
  tours: "Tours", activities: "Activities", other: "Other",
};
const CONTRACT_BADGE = {
  none: { label: "Nije poslan", cls: "border-zinc-600 text-zinc-400" },
  Sent: { label: "Poslan", cls: "border-amber-400/50 text-amber-300" },
  Signed: { label: "Potpisan", cls: "border-emerald-400/50 text-emerald-300" },
  Expired: { label: "Istekao", cls: "border-red-400/50 text-red-300" },
};
const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" · ");
  return "";
};

export function Modal({ children, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className={`w-full ${wide ? "max-w-2xl" : "max-w-md"} my-8 bg-[#0d0d0d] border border-white/10 rounded-2xl p-6`} onClick={(e) => e.stopPropagation()}>{children}</div>
    </div>
  );
}

export default function SuppliersPanel({ prefill, onPrefillConsumed }) {
  const [suppliers, setSuppliers] = useState([]);
  const [adding, setAdding] = useState(null); // null | {} | prefill object
  const [servicesFor, setServicesFor] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/suppliers"); setSuppliers(data || []); }
    catch { /* soft */ }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (prefill) { setAdding(prefill); onPrefillConsumed?.(); }
  }, [prefill, onPrefillConsumed]);

  const sendContract = async (s) => {
    setBusy(s.id + "send");
    try {
      const { data } = await api.post(`/admin/suppliers/${s.id}/contract/send`);
      toast.success(`Ugovor poslan na ${data.sent_to}`);
      load();
    } catch (e) { toast.error(errMsg(e) || "Slanje nije uspjelo"); }
    finally { setBusy(null); }
  };
  const viewContract = async (s) => {
    setBusy(s.id + "view");
    try {
      const { data } = await api.get(`/admin/suppliers/${s.id}/contract/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([data], { type: "application/pdf" })), "_blank");
    } catch { toast.error("PDF nije dostupan"); }
    finally { setBusy(null); }
  };
  const toggleActive = async (s) => {
    const next = s.status === "active" ? "inactive" : "active";
    await api.patch(`/admin/suppliers/${s.id}`, { status: next });
    toast.success(next === "active" ? "Supplier aktiviran" : "Supplier deaktiviran");
    load();
  };
  const remove = async () => {
    if (!deleting) return;
    setBusy("delete");
    try {
      await api.delete(`/admin/suppliers/${deleting.id}`);
      toast.success(`Supplier ${deleting.business_name} trajno obrisan`);
      setDeleting(null); load();
    } catch (e) { toast.error(errMsg(e) || "Brisanje nije uspjelo"); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid="suppliers-panel" className="mt-10">
      <div className="flex items-center gap-2 mb-4">
        <Store className="w-4 h-4 text-[#C5A059]" />
        <h2 className="text-xs uppercase tracking-[0.25em] text-zinc-400">Suppliers — Boats, Quads, Tours &amp; More</h2>
        <button data-testid="add-supplier-btn" onClick={() => setAdding({})}
          className="ml-auto px-4 h-9 rounded-full bg-[#C5A059] text-black text-xs uppercase tracking-widest font-semibold">
          + Add Supplier
        </button>
      </div>

      <div className="space-y-2">
        {suppliers.length === 0 && <p className="text-sm text-zinc-500">No suppliers yet — add one or onboard from an application above.</p>}
        {suppliers.map((s) => (
          <div key={s.id} data-testid={`supplier-card-${s.id}`} className="rounded-xl border border-white/10 bg-[#0d0d0d] p-4 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[220px]">
              <p className="text-white flex items-center gap-2">{s.business_name}
                <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${s.status === "active" ? "border-emerald-400/50 text-emerald-300" : "border-red-400/50 text-red-300"}`}>{s.status}</span>
                <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-white/15 text-zinc-400">{CATEGORY_LABEL[s.category] || s.category}</span>
              </p>
              <p className="text-xs text-zinc-400 mt-0.5">{s.contact_person || "—"} · {s.email} · {s.phone} · provizija {Number(s.commission_pct || 0)}%</p>
              <span data-testid={`supplier-contract-status-${s.id}`}
                className={`mt-1 inline-block text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full border ${(CONTRACT_BADGE[s.contract_status] || CONTRACT_BADGE.none).cls}`}>
                Ugovor: {(CONTRACT_BADGE[s.contract_status] || CONTRACT_BADGE.none).label}
              </span>
            </div>
            <button data-testid={`supplier-services-${s.id}`} onClick={() => setServicesFor(s)}
              className="h-9 px-3 rounded-full border border-[#C5A059]/50 text-[#C5A059] text-xs uppercase tracking-widest flex items-center gap-1"><Tag className="w-3.5 h-3.5" /> Services &amp; Prices</button>
            <button data-testid={`supplier-contract-send-${s.id}`} onClick={() => sendContract(s)} disabled={busy === s.id + "send"}
              className="h-9 px-3 rounded-full border border-white/15 text-zinc-300 hover:border-[#C5A059] text-xs uppercase tracking-widest flex items-center gap-1">
              {busy === s.id + "send" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
              {s.contract_status === "Sent" || s.contract_status === "Expired" ? "Pošalji ponovno" : "Pošalji ugovor"}
            </button>
            <button data-testid={`supplier-contract-view-${s.id}`} onClick={() => viewContract(s)} disabled={busy === s.id + "view"}
              className="h-9 px-3 rounded-full border border-white/15 text-zinc-300 hover:border-[#C5A059] text-xs uppercase tracking-widest flex items-center gap-1">
              {busy === s.id + "view" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5" />} Ugovor PDF
            </button>
            <button data-testid={`supplier-toggle-${s.id}`} onClick={() => toggleActive(s)}
              className={`h-9 px-3 rounded-full border text-xs uppercase tracking-widest flex items-center gap-1 ${s.status === "active" ? "border-red-500/40 text-red-400" : "border-emerald-500/40 text-emerald-400"}`}>
              <Power className="w-3.5 h-3.5" /> {s.status === "active" ? "Deactivate" : "Activate"}
            </button>
            {s.status !== "active" && (
              <button data-testid={`supplier-delete-${s.id}`} onClick={() => setDeleting(s)}
                className="h-9 px-3 rounded-full bg-red-600/90 hover:bg-red-600 text-white text-xs uppercase tracking-widest font-semibold flex items-center gap-1">
                <Trash2 className="w-3.5 h-3.5" /> Obriši
              </button>
            )}
          </div>
        ))}
      </div>

      {adding !== null && <AddSupplierModal prefill={adding} onClose={() => setAdding(null)} onDone={() => { setAdding(null); load(); }} />}
      {servicesFor && <ServicesModal supplier={servicesFor} onClose={() => setServicesFor(null)} />}
      {deleting && (
        <Modal onClose={() => setDeleting(null)}>
          <div data-testid="supplier-delete-confirm-modal">
            <h3 className="text-lg text-white font-light mb-3 flex items-center gap-2"><Trash2 className="w-5 h-5 text-red-400" /> Trajno brisanje suppliera</h3>
            <p className="text-sm text-zinc-300 mb-5">Jeste li sigurni da želite trajno obrisati <span className="text-white">{deleting.business_name}</span>? Briše se profil, cjenik usluga i podaci o ugovoru.</p>
            <div className="flex gap-3">
              <button data-testid="supplier-delete-cancel" onClick={() => setDeleting(null)} className="flex-1 h-11 rounded-xl border border-white/15 text-zinc-300 text-sm uppercase tracking-widest">Odustani</button>
              <button data-testid="supplier-delete-confirm" onClick={remove} disabled={busy === "delete"}
                className="flex-1 h-11 rounded-xl bg-red-600 hover:bg-red-500 text-white font-semibold text-sm uppercase tracking-widest disabled:opacity-50 flex items-center justify-center gap-2">
                {busy === "delete" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Trajno obriši
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function AddSupplierModal({ prefill, onClose, onDone }) {
  const [f, setF] = useState({
    business_name: prefill?.business_name || "",
    category: prefill?.category || "boats",
    contact_person: prefill?.contact_person || "",
    email: prefill?.email || "",
    phone: prefill?.phone || "",
    oib: "", address: "",
    commission_pct: 15,
    notes: prefill?.notes || "",
    send_contract: false,
    source_application_id: prefill?.source_application_id || null,
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/suppliers", { ...f, commission_pct: Number(f.commission_pct) || 15 });
      toast.success(`Supplier ${data.business_name} created${data.contract_sent ? " — ugovor poslan e-mailom" : ""}`);
      onDone();
    } catch (e) { toast.error(errMsg(e) || "Create failed"); }
    finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} wide>
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-lg text-white font-light">Add Supplier</h3>
        <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Business name *</label>
          <input data-testid="supplier-form-business_name" value={f.business_name} onChange={set("business_name")} className={FIELD} /></div>
        <div><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Category</label>
          <select data-testid="supplier-form-category" value={f.category} onChange={set("category")} className={FIELD}>
            {Object.entries(CATEGORY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select></div>
        <div><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Contact person</label>
          <input data-testid="supplier-form-contact_person" value={f.contact_person} onChange={set("contact_person")} className={FIELD} /></div>
        <div><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Email *</label>
          <input data-testid="supplier-form-email" value={f.email} onChange={set("email")} className={FIELD} /></div>
        <div><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Phone (s pozivnim brojem, za WhatsApp) *</label>
          <input data-testid="supplier-form-phone" value={f.phone} onChange={set("phone")} placeholder="+385 91 …" className={FIELD} /></div>
        <div><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">OIB</label>
          <input data-testid="supplier-form-oib" value={f.oib} onChange={set("oib")} className={FIELD} /></div>
        <div className="sm:col-span-2"><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Business address</label>
          <input data-testid="supplier-form-address" value={f.address} onChange={set("address")} className={FIELD} /></div>
        <div><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Agreed commission / deposit %</label>
          <input data-testid="supplier-form-commission" type="number" min="0" max="100" value={f.commission_pct} onChange={set("commission_pct")} className={FIELD} /></div>
        <div className="sm:col-span-2"><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Internal notes</label>
          <textarea data-testid="supplier-form-notes" value={f.notes} onChange={set("notes")} rows={2}
            className="w-full px-3 py-2 bg-[#050505] border border-white/10 rounded-lg text-sm text-white focus:border-[#C5A059] outline-none" /></div>
      </div>
      <label className="flex items-center gap-3 text-sm text-zinc-300 cursor-pointer mt-4">
        <input type="checkbox" data-testid="supplier-form-send-contract" checked={f.send_contract}
          onChange={(e) => setF({ ...f, send_contract: e.target.checked })} className="accent-[#C5A059]" />
        Save &amp; send Supplier Contract immediately (e-potpis link)
      </label>
      <button data-testid="supplier-form-submit" onClick={go} disabled={busy || !f.business_name || !f.email || !f.phone}
        className="w-full h-11 rounded-xl bg-[#C5A059] text-black font-semibold text-sm uppercase tracking-widest disabled:opacity-50 mt-4 flex items-center justify-center gap-2">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {f.send_contract ? "Save & Send Contract" : "Save Supplier"}
      </button>
    </Modal>
  );
}

function ServicesModal({ supplier, onClose }) {
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({ title: "", price_from_eur: "", description: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/admin/suppliers/${supplier.id}/services`).then(({ data }) => setServices(data || [])).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const add = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/suppliers/${supplier.id}/services`, {
        title: form.title, price_from_eur: Number(form.price_from_eur), description: form.description,
      });
      toast.success("Service added");
      setForm({ title: "", price_from_eur: "", description: "" });
      load();
    } catch (e) { toast.error(errMsg(e) || "Add failed"); }
    finally { setBusy(false); }
  };
  const toggle = async (sv) => { await api.patch(`/admin/supplier-services/${sv.id}`, { active: !sv.active }); load(); };
  const del = async (sv) => { await api.delete(`/admin/supplier-services/${sv.id}`); toast.success("Service deleted"); load(); };

  return (
    <Modal onClose={onClose} wide>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg text-white font-light">Services &amp; Prices — {supplier.business_name}</h3>
          <p className="text-xs text-zinc-500 mt-0.5">Aktivne usluge prikazuju se na /experiences stranici ("From €X")</p>
        </div>
        <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
      </div>

      <div className="rounded-xl border border-white/10 bg-black/30 p-4 mb-4 grid sm:grid-cols-[1fr_120px_auto] gap-2 items-end">
        <div><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Service title</label>
          <input data-testid="service-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Blue Cave Tour – speedboat" className={FIELD} /></div>
        <div><label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">From €</label>
          <input data-testid="service-form-price" type="number" min="1" value={form.price_from_eur} onChange={(e) => setForm({ ...form, price_from_eur: e.target.value })} className={FIELD} /></div>
        <button data-testid="service-form-add" onClick={add} disabled={busy || !form.title || !Number(form.price_from_eur)}
          className="h-10 px-4 rounded-full bg-[#C5A059] text-black text-xs uppercase tracking-widest font-semibold disabled:opacity-40 flex items-center gap-1">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Add
        </button>
        <div className="sm:col-span-3"><input data-testid="service-form-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Short public description (optional)" className={FIELD} /></div>
      </div>

      <div className="space-y-2 max-h-[300px] overflow-y-auto">
        {services.length === 0 && <p className="text-sm text-zinc-600">No services yet.</p>}
        {services.map((sv) => (
          <div key={sv.id} data-testid={`service-row-${sv.id}`} className="rounded-xl border border-white/10 bg-black/30 p-3 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white truncate">{sv.title} <span className="text-[#C5A059]">from €{Number(sv.price_from_eur).toFixed(0)}</span></p>
              {sv.description && <p className="text-xs text-zinc-500 truncate">{sv.description}</p>}
            </div>
            <button onClick={() => toggle(sv)} data-testid={`service-toggle-${sv.id}`}
              className={`h-8 px-3 rounded-full border text-[10px] uppercase tracking-widest ${sv.active ? "border-emerald-400/50 text-emerald-300" : "border-zinc-600 text-zinc-500"}`}>
              {sv.active ? "Live" : "Hidden"}
            </button>
            <button onClick={() => del(sv)} data-testid={`service-delete-${sv.id}`} className="h-8 w-8 rounded-full border border-red-500/40 text-red-400 flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-zinc-600 mt-3 flex items-center gap-1"><FileText className="w-3 h-3" /> Deposit charged to the customer = {Number(supplier.commission_pct || 15)}% of the service price.</p>
    </Modal>
  );
}
